import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ResponsiveContentWrapper from "../layout/ResponsiveContentWrapper";
import { getAccessibleMenu, menuConfig } from "../../config/menuConfig";
import MfaReminderDialog from "../auth/mfaReminderDialog";
import "./Landing.css";

// IoT network background (central house + connected devices)
import imgLandingBg from "../../assets/landing-bg-iot.png";

/**
 * Capitalize first letter of each word; rest lowercase.
 * Works for "gudi suresh", "GUDI SURESH", or username like "gudi.suresh".
 */
const formatDisplayName = (firstName, lastName, username) => {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (first || last) {
    const parts = [];
    if (first) parts.push(first.charAt(0).toUpperCase() + first.slice(1).toLowerCase());
    if (last) parts.push(last.charAt(0).toUpperCase() + last.slice(1).toLowerCase());
    return parts.join(" ").trim();
  }
  if (!username) return "";
  const parts = String(username).split(/[._\s]+/).filter(Boolean);
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ").trim();
};

/**
 * Lightweight dashboard-style SVG for hero (no external assets).
 * Grid + devices metaphor - minimal and professional.
 */
const HeroIllustration = () => (
  <svg
    width="120"
    height="80"
    viewBox="0 0 120 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ maxWidth: "100%", height: "auto" }}
  >
    <rect x="8" y="8" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.9" />
    <rect x="8" y="40" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.7" />
    <rect x="76" y="8" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.7" />
    <rect x="76" y="40" width="36" height="24" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.9" />
    <circle cx="26" cy="20" r="2.5" fill="currentColor" opacity="0.6" />
    <circle cx="94" cy="52" r="2.5" fill="currentColor" opacity="0.6" />
    <path d="M44 20h28M44 52h28" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
  </svg>
);

/**
 * Default landing page shown after login for all user roles.
 * Renders feature sections (aligned with sidebar menu) and shows only
 * sections/items the user has access to (UI-level conditional rendering).
 * Enhanced with subtle animations, section icons, and a clean hero area.
 */
const Landing = ({ userLevel, userRoles, tenantInfo, firstName, lastName, username }) => {
  const userContext = useMemo(
    () => ({
      userLevel: userLevel ?? 0,
      roles: userRoles ?? [],
      tenantId: tenantInfo?.tenantId ?? "",
    }),
    [userLevel, userRoles, tenantInfo]
  );

  const accessibleMenu = useMemo(
    () => getAccessibleMenu(menuConfig, userContext),
    [userContext]
  );

  const displayName = useMemo(
    () => formatDisplayName(firstName, lastName, username),
    [firstName, lastName, username]
  );

  return (
    <>
    <MfaReminderDialog />
    <ResponsiveContentWrapper
      className="landing-content-wrapper"
      sx={{
        bgcolor: "#b0b0b0",
        padding: { xs: 1, sm: 1.5, md: 2.25 },
        paddingLeft: { xs: 1, sm: 1.5, md: 2.25 },
        marginTop: "64px",
        height: "calc(100vh - 64px)",
        maxHeight: "calc(100vh - 64px)",
        /* Always allow vertical scroll when content overflows (with or without sidebar) */
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Box
        className="landing-page-root"
        style={{ backgroundColor: "#b0b0b0", marginTop: 0 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          /* Let content grow so scroll appears whenever it overflows (sidebar or no sidebar) */
          flex: "none",
          minHeight: "auto",
          overflow: "visible",
          pt: 0,
          pb: 0,
          px: 0,
          mt: 0,
          bgcolor: "#b0b0b0",
        }}
      >
        {/* Hero: IoT background image with welcome layout overlay (2nd image style) */}
        <Box
          className="landing-hero"
          sx={{
            position: "relative",
            borderRadius: { xs: 1.5, sm: 2 },
            overflow: "hidden",
            marginTop: 0,
            marginBottom: { xs: 1, sm: 1.5, md: 2.25 },
            /* Responsive hero height: mobile → tablet → desktop */
            height: { xs: 88, sm: 100, md: 110, lg: 120 },
            minHeight: { xs: 88, sm: 100, md: 110, lg: 120 },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            backgroundImage: `url(${imgLandingBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark overlay so text stays readable */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.45) 100%)",
              pointerEvents: "none",
            }}
          />
          {/* Content: Welcome left + grid right (like 2nd image) */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: { xs: 1, sm: 2, md: 2.5 },
              width: "100%",
              px: { xs: 1.25, sm: 2, md: 2.5 },
              py: { xs: 1, sm: 1.25, md: 1.5 },
            }}
          >
            <Box sx={{ flex: { xs: "1 1 auto", sm: "1 1 260px" }, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
              <Typography
                component="h1"
                variant="h5"
                sx={{
                  fontWeight: 600,
                  marginBottom: 0.25,
                  color: "#fff",
                  letterSpacing: "-0.02em",
                  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
                }}
              >
                Welcome{displayName ? `, ${displayName}` : ""}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: { xs: "100%", sm: 360, md: 420 },
                  lineHeight: 1.4,
                  color: "rgba(255,255,255,0.92)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
                }}
              >
                Choose a section below to get started. You only see areas you have access to.
              </Typography>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                color: "rgba(255,255,255,0.95)",
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HeroIllustration />
            </Box>
          </Box>
        </Box>

        {/* Section cards: content grows so wrapper shows scroll when overflow (any screen size) */}
        <Box
          sx={{
            flex: "none",
            minHeight: "auto",
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
            overflowY: "visible",
          }}
        >
          <Grid
            container
            spacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
            sx={{
              flex: "none",
              alignContent: "flex-start",
              minHeight: "auto",
              pb: { xs: 2, md: 0 },
            }}
          >
            {accessibleMenu.map((section, index) => {
              const SectionIcon = section.icon;
              return (
                <Grid item xs={12} sm={6} md={6} lg={4} key={section.id}>
                  <Card
                    className="landing-card"
                    variant="outlined"
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: { xs: 1.5, md: 2 },
                      borderWidth: 1,
                      animationDelay: `${index * 0.06}s`,
                      "&:hover": {
                        boxShadow: 3,
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        "&:last-child": { pb: 1 },
                        pt: { xs: 1.25, md: 1 },
                        px: { xs: 1.25, sm: 1.5 },
                        pb: { xs: 1.25, md: 1 },
                      }}
                    >
                      <Box
                        className="landing-card-header"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: { xs: 0.75, sm: 0.75 },
                          marginBottom: { xs: 0.5, md: 0.75 },
                          minHeight: { xs: 32, sm: 36 },
                          pl: 0,
                          marginLeft: 0,
                        }}
                      >
                        <Box
                          className="landing-card-section-icon"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: { xs: 32, sm: 36 },
                            minWidth: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            flexShrink: 0,
                            borderRadius: 1,
                            bgcolor: "#fff3e0",
                            color: "#ed6c02",
                          }}
                        >
                          {SectionIcon && <SectionIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                        </Box>
                        <Typography
                          component="h2"
                          variant="h6"
                          sx={{
                            fontSize: { xs: 15, sm: 16 },
                            fontWeight: 600,
                            color: "text.primary",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {section.name}
                        </Typography>
                      </Box>
                      <List disablePadding className="landing-card-list" sx={{ pt: 0 }}>
                        {section.subitems?.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <ListItem
                              key={item.id}
                              component={Link}
                              to={item.path}
                              disablePadding
                              className="landing-link-item"
                              sx={{
                                color: "#ed6c02",
                                textDecoration: "none",
                                borderRadius: 1,
                                py: { xs: 0.75, md: 0.5 },
                                pl: 0,
                                /* Touch-friendly min height on mobile (44px) */
                                minHeight: { xs: 44, md: 36 },
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                                "&:hover .landing-submenu-icon": {
                                  color: "#ed6c02",
                                },
                              }}
                            >
                              <ListItemIcon
                                className="landing-submenu-icon landing-card-subitem-icon"
                                sx={{
                                  minWidth: { xs: 36, md: 36 },
                                  width: { xs: 36, md: 36 },
                                  paddingLeft: 0,
                                  paddingRight: 0,
                                  color: "#757575",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "color 0.2s ease",
                                }}
                              >
                                {ItemIcon ? <ItemIcon sx={{ fontSize: { xs: 20, md: 22 } }} /> : null}
                              </ListItemIcon>
                              <ListItemText
                                primary={item.name}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontSize: { xs: 13, sm: 14 },
                                  fontWeight: 500,
                                }}
                                sx={{ m: 0 }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {accessibleMenu.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            className="landing-hero"
            sx={{ mt: 2 }}
          >
            No sections are available for your role. Contact your administrator if you need access.
          </Typography>
        )}
      </Box>
    </ResponsiveContentWrapper>
    </>
  );
};

const mapStateToProps = (state) => ({
  userLevel: state.auth.userLevel,
  userRoles: state.auth.userRoles,
  tenantInfo: state.tenant.tenantInfo,
  firstName: state.auth.firstName,
  lastName: state.auth.lastName,
  username: state.auth.username,
});

export default connect(mapStateToProps)(Landing);
